import { useEffect, useMemo, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import ApprovalStatusChip from 'components/chip/ApprovalStatusChip';
import TaskStatusChip from 'components/chip/TaskStatusChip';
import TicketCategoryChip from 'components/chip/TicketCategoryChip';
import TicketPriorityChip from 'components/chip/TicketPriorityChip';
import TicketScopeChip from 'components/chip/TicketScopeChip';
import DateTimePicker from 'components/form/DateTimePicker';
import UserSelect from 'components/form/UserSelect';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import {
  ApprovalStatus,
  TaskStatus,
  TicketDetailResult,
  approveTicket,
  updateApprovalStatus,
  updateTaskAssignee,
  updateTaskSchedule,
  updateTaskStatus,
} from 'stores/TicketStoreAdapter';
import { addTicketWatch, removeTicketWatch } from 'stores/TicketWatchStoreAdapter';
import {
  SENTINEL_DATE,
  formatDate,
  formatDateOrDash,
  formatDateTime,
  isSentinelDate,
} from 'utils/DateTimeFormatter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

type Props = {
  detail: TicketDetailResult;
  /** 優先度マスタ（API9001: priority）で名前解決 */
  priorities: MasterTypeResult[];
  /** ステータス操作/ウォッチ後に詳細を再取得する */
  onUpdated: () => void;
  /** 編集ボタン押下 */
  onEdit: () => void;
  /** 親チケットへの遷移（親子パンくず用） */
  onOpenTicket?: (ticketId: string) => void;
};

const TASK_STATUSES: TaskStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'DONE', 'CLOSED'];

/**
 * チケット詳細の本文（ヘッダー固定 + 基本情報 + ガント情報 + フォーム自由項目 + 操作エリア）。
 * ドロワー / direct-link で共有する。
 */
const TicketDetail: React.FC<Props> = ({ detail, priorities, onUpdated, onEdit, onOpenTicket }) => {
  const { userSession, users } = useAuth();
  const [busy, setBusy] = useState(false);

  // スケジュール（予定/実績日）のインライン編集（D5-3 / BUC-TK12: 詳細からも編集可能にする）
  const [ganttEdit, setGanttEdit] = useState(false);
  const [schedStart, setSchedStart] = useState('');
  const [schedEnd, setSchedEnd] = useState('');
  const [actualStart, setActualStart] = useState('');
  const [actualEnd, setActualEnd] = useState('');

  // 担当者のインライン編集（タスクのみ・API7112）
  const [assigneeEdit, setAssigneeEdit] = useState(false);
  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(null);

  useEffect(() => {
    const norm = (v: string | null) => (v && !isSentinelDate(v) ? v : '');
    setSchedStart(norm(detail.scheduledStartDate));
    setSchedEnd(norm(detail.scheduledEndDate));
    setActualStart(detail.actualStartDate ?? '');
    setActualEnd(detail.actualEndDate ?? '');
    setGanttEdit(false);
    setAssigneeUserId(detail.assigneeUserId);
    setAssigneeEdit(false);
  }, [detail]);

  const isTask = detail.ticketCategory === 'TASK';
  const isGlobal = detail.scope === 'GLOBAL';
  const priorityLabel = useMemo(
    () => priorities.find((p) => p.typeCode === detail.priority)?.typeLabel,
    [priorities, detail.priority],
  );

  const isCreator = detail.creatorUserId === userSession.userId;
  const isApprover = detail.approverUserId === userSession.userId;
  const isSelfApproval = detail.creatorUserId === detail.approverUserId;

  // 編集可否:
  // - タスク: 可視なら編集可（PRIVATE所有者制約はBEが担保）
  // - 承認: 作成者本人 かつ status が ADJUSTING/REJECTED のときのみ（UC-TK17）
  const canEdit = isTask
    ? true
    : isCreator && (detail.status === 'ADJUSTING' || detail.status === 'REJECTED');

  const run = (fn: () => Promise<void>, okMsg: string) => {
    if (busy) return;
    setBusy(true);
    fn()
      .then(() => {
        successDefault(okMsg);
        onUpdated();
      })
      .catch((e) => errorDefault('操作に失敗しました', toErrorDetail(e)))
      .finally(() => setBusy(false));
  };

  const handleTaskStatus = (status: TaskStatus) =>
    run(() => updateTaskStatus({ ticketId: detail.ticketId, status }), 'ステータスを更新しました');

  const handleApprovalStatus = (
    targetStatus: 'SUBMITTED' | 'IN_PROGRESS' | 'REJECTED' | 'CANCELLED',
  ) =>
    run(
      () => updateApprovalStatus({ ticketId: detail.ticketId, targetStatus }),
      'ステータスを更新しました',
    );

  const handleApprove = () =>
    run(() => approveTicket({ ticketId: detail.ticketId }), '承認しました');

  const handleWatchToggle = () =>
    run(
      () =>
        detail.watchedByMe
          ? removeTicketWatch({ ticketId: detail.ticketId })
          : addTicketWatch({ ticketId: detail.ticketId }),
      detail.watchedByMe ? 'ウォッチを解除しました' : 'ウォッチしました',
    );

  const handleSaveGantt = () =>
    run(
      () =>
        updateTaskSchedule({
          schedules: [
            {
              ticketId: detail.ticketId,
              scheduledStartDate: schedStart || undefined,
              // 予定終了日は空ならセンチネル（未設定）に戻す
              scheduledEndDate: schedEnd || `${SENTINEL_DATE}T23:59:59`,
              actualStartDate: actualStart || undefined,
              actualEndDate: actualEnd || undefined,
            },
          ],
        }),
      'スケジュールを保存しました',
    );

  const handleSaveAssignee = () => {
    if (!assigneeUserId) return;
    run(
      () => updateTaskAssignee({ ticketId: detail.ticketId, assigneeUserId }),
      '担当者を変更しました',
    );
    setAssigneeEdit(false);
  };

  const approvalStatus = detail.status as ApprovalStatus;

  return (
    <Box>
      {/* ヘッダー（固定的な見出し） */}
      <Box data-toc-section id="ticket-header" data-toc-label="概要">
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="h6" fontWeight={700}>
            {detail.managementId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {detail.typeName}
          </Typography>
          <TicketCategoryChip ticketCategory={detail.ticketCategory} />
          <TicketScopeChip scope={detail.scope} />
          {isTask ? (
            <TaskStatusChip status={detail.status as TaskStatus} />
          ) : (
            <ApprovalStatusChip status={approvalStatus} />
          )}
          <Box sx={{ flex: 1 }} />
          {isGlobal && (
            <Button
              size="small"
              startIcon={detail.watchedByMe ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={handleWatchToggle}
              disabled={busy}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {detail.watchedByMe ? 'ウォッチ解除' : 'ウォッチ'}
            </Button>
          )}
          {canEdit && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEdit}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
            >
              編集
            </Button>
          )}
        </Stack>
        {detail.parentTicket && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
            {onOpenTicket ? (
              <Typography
                component="button"
                variant="caption"
                onClick={() => onOpenTicket(detail.parentTicket!.ticketId)}
                sx={{
                  border: 0,
                  p: 0,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  color: 'primary.main',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {detail.parentTicket.managementId}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {detail.parentTicket.managementId}
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled">
              /
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {detail.managementId}
            </Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            作成: {detail.creatorUserName}・{formatDate(detail.createdAt)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            更新: {formatDateTime(detail.updatedAt)}
          </Typography>
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* タイトル */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        {detail.title}
      </Typography>

      {/* 操作（ステータス） */}
      <Box data-toc-section id="ticket-actions" data-toc-label="操作" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          ステータス
        </Typography>
        {isTask ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {TASK_STATUSES.map((s) => (
              <Button
                key={s}
                size="small"
                variant={detail.status === s ? 'contained' : 'outlined'}
                disabled={busy || detail.status === s}
                onClick={() => handleTaskStatus(s)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
              >
                {TaskStatus[s]}
              </Button>
            ))}
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {approvalStatus === 'ADJUSTING' && isCreator && (
              <>
                <ActionButton
                  label="申請"
                  onClick={() => handleApprovalStatus('SUBMITTED')}
                  busy={busy}
                />
                <ActionButton
                  label="取消"
                  onClick={() => handleApprovalStatus('CANCELLED')}
                  busy={busy}
                />
              </>
            )}
            {approvalStatus === 'SUBMITTED' && isApprover && (
              <ActionButton
                label="対応開始"
                onClick={() => handleApprovalStatus('IN_PROGRESS')}
                busy={busy}
              />
            )}
            {approvalStatus === 'IN_PROGRESS' && isApprover && (
              <>
                <ActionButton
                  label="承認"
                  color="success"
                  onClick={handleApprove}
                  busy={busy}
                  disabled={isSelfApproval}
                />
                <ActionButton
                  label="否決"
                  color="error"
                  onClick={() => handleApprovalStatus('REJECTED')}
                  busy={busy}
                  disabled={isSelfApproval}
                />
              </>
            )}
            {approvalStatus === 'REJECTED' && isCreator && (
              <>
                <ActionButton
                  label="再申請"
                  onClick={() => handleApprovalStatus('SUBMITTED')}
                  busy={busy}
                />
                <ActionButton
                  label="取消"
                  onClick={() => handleApprovalStatus('CANCELLED')}
                  busy={busy}
                />
              </>
            )}
            {isSelfApproval && approvalStatus === 'IN_PROGRESS' && (
              <Typography variant="caption" color="text.secondary">
                自己承認・自己否決はできません
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      {/* 担当者・優先度・承認者 */}
      <Box data-toc-section id="ticket-basic" data-toc-label="担当者" sx={{ mb: 2 }}>
        <Stack spacing={0.75}>
          {/* 担当者（タスクは詳細から直接変更可・API7112） */}
          <PropRow label="担当者">
            {isTask && assigneeEdit ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <UserSelect
                    mode="single"
                    label="担当者"
                    required
                    users={users}
                    selectedUserId={assigneeUserId}
                    onChange={setAssigneeUserId}
                  />
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy || !assigneeUserId}
                  onClick={handleSaveAssignee}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                  }}
                >
                  保存
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setAssigneeUserId(detail.assigneeUserId);
                    setAssigneeEdit(false);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  取消
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">{detail.assigneeUserName}</Typography>
                {isTask && (
                  <Button
                    size="small"
                    startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                    onClick={() => setAssigneeEdit(true)}
                    sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0 }}
                  >
                    変更
                  </Button>
                )}
              </Stack>
            )}
          </PropRow>
          {isTask && detail.priority && (
            <PropRow label="優先度">
              <TicketPriorityChip code={detail.priority} label={priorityLabel} />
            </PropRow>
          )}
          {!isTask && detail.approverUserName && (
            <PropRow label="承認者">
              <Typography variant="body2">{detail.approverUserName}</Typography>
            </PropRow>
          )}
        </Stack>
      </Box>

      {/* スケジュール（タスクのみ・インライン編集可） */}
      {isTask && (
        <Box data-toc-section id="ticket-gantt" data-toc-label="スケジュール" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
              スケジュール
            </Typography>
            {!ganttEdit && (
              <Button
                size="small"
                startIcon={<EventIcon sx={{ fontSize: 16 }} />}
                onClick={() => setGanttEdit(true)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                スケジュールを編集
              </Button>
            )}
          </Stack>
          {ganttEdit ? (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <DateTimePicker
                  mode="start"
                  label="開始予定日"
                  value={schedStart}
                  onChange={setSchedStart}
                  fullWidth
                />
                <DateTimePicker
                  mode="end"
                  label="終了予定日"
                  value={schedEnd}
                  onChange={setSchedEnd}
                  fullWidth
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <DateTimePicker
                  mode="start"
                  label="開始日"
                  value={actualStart}
                  onChange={setActualStart}
                  fullWidth
                />
                <DateTimePicker
                  mode="end"
                  label="終了日"
                  value={actualEnd}
                  onChange={setActualEnd}
                  fullWidth
                />
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={() => setGanttEdit(false)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  キャンセル
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy}
                  onClick={handleSaveGantt}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                  }}
                >
                  保存
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1}>
              {/* 予定（開始予定日・終了予定日）を横並び */}
              <Stack direction="row" spacing={2}>
                <DateField
                  label="開始予定日"
                  value={formatDateOrDash(detail.scheduledStartDate, '-')}
                />
                <DateField label="終了予定日" value={formatDateOrDash(detail.scheduledEndDate)} />
              </Stack>
              {/* 実績（開始日・終了日）を横並び */}
              <Stack direction="row" spacing={2}>
                <DateField label="開始日" value={formatDateOrDash(detail.actualStartDate, '-')} />
                <DateField label="終了日" value={formatDateOrDash(detail.actualEndDate, '-')} />
              </Stack>
            </Stack>
          )}
        </Box>
      )}

      {/* 説明（担当者・スケジュールの下） */}
      <Box data-toc-section id="ticket-description" data-toc-label="説明" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          説明
        </Typography>
        {detail.description ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {detail.description}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">
            説明はありません
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const PropRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" spacing={2} alignItems="center">
    <Typography variant="body2" color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

/** スケジュールの日付表示（ラベル上・値下の縦積みを横並びで2列にする用） */
const DateField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const ActionButton: React.FC<{
  label: string;
  onClick: () => void;
  busy: boolean;
  color?: 'primary' | 'success' | 'error';
  disabled?: boolean;
}> = ({ label, onClick, busy, color = 'primary', disabled }) => (
  <Button
    size="small"
    variant="contained"
    color={color}
    disabled={busy || disabled}
    onClick={onClick}
    sx={{
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      boxShadow: 'none',
      '&:hover': { boxShadow: 'none' },
    }}
  >
    {label}
  </Button>
);

export default TicketDetail;
